/**
 * The fee *structure*: which domain each knob belongs to, so the page reads as a shape
 * rather than as a list of eight things in whatever order the API returned them.
 *
 * Eight rows in one flat table is not a structure, it is an inventory. An operator asking
 * "what do we charge on bills?" had to read every label to find out that two of them were
 * about bills, and the two instant-settlement knobs sat next to the crypto withdrawal fee
 * with nothing to say they were a pair. Grouping does not change a single value, a single
 * key, or a single request: it changes what an operator can find.
 *
 * ## Why the grouping lives here and not on the server
 *
 * The server owns the *catalogue* — the label, the description, the ceiling, and whether a
 * knob inherits — and the console renders whatever it is sent, so a new knob appears without
 * an admin release. What the server does not carry is a domain, and inventing a field for it
 * would be a contract change to move a heading. So the mapping lives here, and the fallback
 * below is what keeps the no-release-needed property intact: an unrecognised key is placed
 * by its prefix, and a key that matches no prefix lands in a plainly labelled group of its
 * own rather than vanishing off the page.
 *
 * That fallback is the important half. A page that only rendered keys it recognised would
 * hide a live fee, and a hidden fee is one nobody can answer a customer about.
 */
import type { AdminFee, MoneyDTO, ProviderCost } from '../contracts/types.js';

export type FeeDomain = 'trading' | 'conversion' | 'crypto' | 'bills' | 'settlement' | 'other';

export interface FeeDomainInfo {
  readonly id: FeeDomain;
  readonly label: string;
  /** One line saying what this group prices, in an operator's words. */
  readonly hint: string;
}

/** The order the page prints them in: the customer's journey, roughly, then the leftovers. */
export const FEE_DOMAINS: readonly FeeDomainInfo[] = [
  {
    id: 'trading',
    label: 'Trading',
    hint: 'What the house adds to the market price, and what a swap costs on top of it.',
  },
  {
    id: 'conversion',
    label: 'Conversion',
    hint: 'Moving between two balances the customer already holds.',
  },
  {
    id: 'crypto',
    label: 'Crypto',
    hint: 'What leaves the platform on a chain, and what we charge to send it.',
  },
  {
    id: 'bills',
    label: 'Bills',
    hint: 'The convenience fee on a bill payment, and the ceiling it can never pass.',
  },
  {
    id: 'settlement',
    label: 'Settlement',
    hint: 'Instant settlement. Both knobs track the manual pair above until you separate them.',
  },
  {
    id: 'other',
    label: 'Other fees',
    hint: 'Knobs this console does not have a heading for yet. They are live and editable all the same.',
  },
];

/** Every key the contract publishes today, placed by hand. */
const EXPLICIT: Readonly<Record<string, FeeDomain>> = {
  spread_bps: 'trading',
  swap_fee_bps: 'trading',
  conversion_fee_bps: 'conversion',
  crypto_withdrawal_fee_bps: 'crypto',
  bill_fee_bps: 'bills',
  bill_fee_cap_kobo: 'bills',
  instant_settlement_fee_bps: 'settlement',
  instant_settlement_spread_bps: 'settlement',
};

/**
 * The fallback, longest prefix first.
 *
 * `instant_settlement_` has to be tested before anything shorter could match it, which is
 * why this is an ordered list rather than an object: object key order is a language detail
 * to lean on, and "the fee ends up under the wrong heading" is a silent enough failure that
 * it would go unnoticed for a long time.
 */
const PREFIXES: readonly (readonly [string, FeeDomain])[] = [
  ['instant_settlement_', 'settlement'],
  ['settlement_', 'settlement'],
  ['conversion_', 'conversion'],
  ['crypto_', 'crypto'],
  ['bill_', 'bills'],
  ['swap_', 'trading'],
  ['spread_', 'trading'],
  ['trade_', 'trading'],
];

/** Which group a key belongs to. Never throws, never returns nothing. */
export function domainOf(key: string): FeeDomain {
  const explicit = EXPLICIT[key];
  if (explicit !== undefined) return explicit;
  for (const [prefix, domain] of PREFIXES) {
    if (key.startsWith(prefix)) return domain;
  }
  return 'other';
}

export interface FeeGroup {
  readonly domain: FeeDomainInfo;
  readonly fees: readonly AdminFee[];
}

/**
 * The board, grouped and in a stable order.
 *
 * An empty group is dropped rather than rendered as a heading with nothing under it: the
 * catalogue is the server's, so a deployment that does not carry a bill schedule should show
 * no Bills heading rather than an empty promise of one.
 *
 * Within a group the API's own order is preserved. It is the catalogue's order, it is stable
 * across reloads, and re-sorting alphabetically here would put the bill cap above the bill
 * rate it caps.
 */
export function groupFees(fees: readonly AdminFee[]): readonly FeeGroup[] {
  const byDomain = new Map<FeeDomain, AdminFee[]>();
  for (const fee of fees) {
    const domain = domainOf(fee.key);
    const bucket = byDomain.get(domain);
    if (bucket === undefined) byDomain.set(domain, [fee]);
    else bucket.push(fee);
  }
  const groups: FeeGroup[] = [];
  for (const domain of FEE_DOMAINS) {
    const bucket = byDomain.get(domain.id);
    if (bucket !== undefined && bucket.length > 0) groups.push({ domain, fees: bucket });
  }
  return groups;
}

// ── What the vendors charged, added up ───────────────────────────────────────

/**
 * The total an outside party billed across a page of transactions, or null.
 *
 * The contract names this reduction on `adminTxRowSchema.providerCost`: the per-row figure is
 * the revenue's opposite number, and the sum is what a finance lead reads at the bottom of the
 * column. It is here rather than in `money.ts` because it is about *fees* — what the desk paid
 * out — and because the one rule that makes it correct is a fee rule rather than a money one.
 *
 * ## Why a mixed-currency page returns null rather than a number
 *
 * A page can hold a naira PSP fee and a BTC network fee at once, and adding those together
 * would produce a figure in no currency at all — printed under a naira symbol, it would be a
 * confident lie about how much the desk spent. So the total is offered only when every
 * captured cost is in one currency, and the caller renders nothing otherwise.
 *
 * Rows with no captured cost are skipped, not counted as zero. "Not captured yet" and "no fee"
 * are different facts (a PSP's cut is sometimes only known on settlement), and folding the
 * first into the second is how a partial total gets read as a complete one.
 */
export function totalProviderFees(
  rows: readonly { readonly providerCost: ProviderCost | null }[],
): MoneyDTO | null {
  let currency: string | null = null;
  let total = 0n;
  let counted = 0;

  for (const row of rows) {
    const cost = row.providerCost;
    if (cost === null) continue;
    if (currency === null) currency = cost.amount.currency;
    else if (currency !== cost.amount.currency) return null;
    try {
      total += BigInt(cost.amount.amount);
    } catch {
      // An amount that is not an integer string is a server we cannot add up. Refusing the
      // whole total is the honest answer; a partial one would be indistinguishable from a
      // complete one on screen.
      return null;
    }
    counted += 1;
  }

  if (counted === 0 || currency === null) return null;
  return { currency: currency as MoneyDTO['currency'], amount: total.toString() };
}
